import { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Plus, Bookmark, Eye, Star, Heart, Check, ChevronRight, Sparkles, List, Send, Share2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useChat } from '../contexts/ChatContext';
import { useUserLibrary, type MediaEnrichment } from '../contexts/UserLibraryContext';
import { useLists, type WatchlistMeta as Watchlist } from '../contexts/ListsContext';
import { useUserRating } from '../hooks/useRatings';
import { useToast } from './ui/Toast';
import { CreateListModal, type MediaFilter } from './CreateListModal';
import { RecommendModal } from './RecommendModal';
import { ShareModal } from './ShareModal';
import { buildShareUrl } from '../hooks/useShare';
import { useCustomListMembershipBatch } from '../hooks/useListsQuery';
import { SocialUpgradeModal } from './ui/SocialUpgradeModal';
import { effectiveTier } from '../utils/entitlements';
import { ApiError } from '../lib/api';
import type { MediaItem } from '../types/movie';
import { calculateOurRating } from '../utils/helpers';

interface QuickActionButtonProps {
  movie: MediaItem;
  onRateClick?: () => void;
  className?: string;
  onOpenChange?: (isOpen: boolean) => void; // Notify parent when menu opens/closes
}

type SubMenuType = 'addTo' | 'customLists' | null;

// Position constants
const TOPBAR_H = 64;
const PAD = 8;
const MENU_W = 224;
const SUB_W = 208;

export function QuickActionButton({ movie, onRateClick, className = '', onOpenChange }: QuickActionButtonProps) {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const { openWithQuestion } = useChat();
  const { toast } = useToast();
  const isFree = !!profile && effectiveTier(profile) === 'free';
  const [upgradeModalFeature, setUpgradeModalFeature] = useState<string | null>(null);
  // ListsContext owns list metadata + mutations
  // TASK D: Removed isItemPending - no grey pending UX
  // TASK 1: Use isInList from ListsContext as primary source (reads optimistic React Query cache)
  // Phase 5: Use setListItemDesired for SPAM-SAFE custom list operations
  const { lists: watchlists, createWatchlist, toggleListItem, setListItemDesired, isInList: optimisticIsInList } = useLists();

  // Default list membership stays in UserLibraryContext
  const {
    isWatched,
    isInWatchlist,
    isInFavorites,
    toggleWatched,
    toggleWatchlist,
    toggleFavorites,
  } = useUserLibrary();

  const mediaType = movie.media_type || 'movie';

  // React Query for user rating (single source of truth)
  const { rating: userRating } = useUserRating(movie.id, mediaType);

  // UI-only state (menus, modals)
  const [isOpen, setIsOpen] = useState(false);
  const [activeSubMenu, setActiveSubMenu] = useState<SubMenuType>(null);
  const [showCreateListModal, setShowCreateListModal] = useState(false);
  const [showRecommendModal, setShowRecommendModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [submenuSide, setSubmenuSide] = useState<'right' | 'left'>('right');

  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const anchorPointRef = useRef<{ x: number; y: number } | null>(null);

  // Get custom lists (non-default) that are compatible with the current media type
  // Memoize to prevent infinite render loops in useEffect
  const customLists = useMemo(() =>
    watchlists.filter((w: Watchlist) =>
      !w.is_default &&
      (!w.media_filter || w.media_filter === 'mixed' || w.media_filter === mediaType)
    ),
    [watchlists, mediaType]
  );

  // Batch membership check for custom lists (10M scale - single query instead of N fetches)
  const customListIds = useMemo(() => customLists.map((l) => l.id), [customLists]);
  const { isInList: serverIsInList } = useCustomListMembershipBatch(
    movie.id,
    mediaType,
    customListIds,
    { enabled: isOpen && customListIds.length > 0 }
  );

  // TASK 1: Compute unified isInCustomList - optimistic cache first, server fallback
  // This ensures UI state matches what the mutation will read, preventing wrong-branch toggles
  const isInCustomList = useCallback((listId: string) => {
    // Optimistic cache is primary (matches what toggleListItem reads)
    const optimistic = optimisticIsInList(listId, movie.id, mediaType);
    // Server membership is fallback for cold cache
    const server = serverIsInList(listId);
    return optimistic || server;
  }, [optimisticIsInList, movie.id, mediaType, serverIsInList]);

  // Check if trigger has scrolled out of view - close menu if so
  const checkTriggerVisibility = useCallback(() => {
    if (!buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();

    // Detect filter bar bottom
    const filterBar = document.querySelector('[data-filter-bar]');
    const filterBottom =
      filterBar instanceof HTMLElement
        ? filterBar.getBoundingClientRect().bottom
        : TOPBAR_H;

    // Close if trigger is behind TopBar/FilterBar or below viewport
    if (rect.bottom < filterBottom || rect.top > window.innerHeight) {
      setIsOpen(false);
      setActiveSubMenu(null);
    }
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setActiveSubMenu(null);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Reset anchor point when menu closes
  useEffect(() => {
    if (!isOpen) anchorPointRef.current = null;
  }, [isOpen]);

  // Position menu ONCE on open using PAGE coordinates (scrolls with content)
  useLayoutEffect(() => {
    if (!isOpen || !menuRef.current || !buttonRef.current) return;

    // Capture button position in PAGE coordinates ONCE on open
    if (!anchorPointRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      anchorPointRef.current = {
        x: rect.right + window.scrollX,
        y: rect.bottom + window.scrollY,
      };
    }
    const anchor = anchorPointRef.current;

    let raf = 0;
    let tries = 0;

    // Keep hidden until positioned
    menuRef.current.style.opacity = '0';

    const positionOnce = () => {
      if (!menuRef.current) return;

      const menuH = menuRef.current.offsetHeight;
      if (menuH === 0 && tries++ < 6) {
        raf = requestAnimationFrame(positionOnce);
        return;
      }

      // Detect filter bar bottom (viewport coords for overlap check)
      const filterBar = document.querySelector('[data-filter-bar]');
      const filterBottom =
        filterBar instanceof HTMLElement
          ? filterBar.getBoundingClientRect().bottom
          : TOPBAR_H;

      // Convert anchor to current viewport coords to check filter bar overlap
      const viewportTop = anchor.y - window.scrollY + PAD;
      if (viewportTop < filterBottom + PAD) {
        setIsOpen(false);
        setActiveSubMenu(null);
        return;
      }

      // Compute left in page coordinates, clamped to visible viewport
      let left = anchor.x - MENU_W;
      const minLeft = window.scrollX + PAD;
      const maxLeft = window.scrollX + window.innerWidth - MENU_W - PAD;
      left = Math.max(minLeft, Math.min(left, maxLeft));

      // Top in page coordinates
      const top = anchor.y + PAD;

      // Set position ONCE (page coordinates, scrolls with content)
      menuRef.current.style.transform = `translate3d(${left}px, ${top}px, 0)`;
      menuRef.current.style.opacity = '1';
    };

    raf = requestAnimationFrame(positionOnce);
    return () => cancelAnimationFrame(raf);
  }, [isOpen, activeSubMenu]);

  // Scroll listener ONLY checks visibility and closes - no repositioning
  useEffect(() => {
    if (isOpen) {
      window.addEventListener('scroll', checkTriggerVisibility, true);
      return () => {
        window.removeEventListener('scroll', checkTriggerVisibility, true);
      };
    }
  }, [isOpen, checkTriggerVisibility]);

  // Notify parent when menu opens/closes
  useEffect(() => {
    onOpenChange?.(isOpen);
  }, [isOpen, onOpenChange]);

  // NOTE: No fetchListItems effect needed - useCustomListMembershipBatch handles membership checks

  // Calculate submenu side based on viewport space
  useEffect(() => {
    if (activeSubMenu !== 'addTo' || !menuRef.current) return;
    const rect = menuRef.current.getBoundingClientRect();
    const wouldOverflowRight = rect.right + SUB_W + PAD > window.innerWidth;
    setSubmenuSide(wouldOverflowRight ? 'left' : 'right');
  }, [activeSubMenu]);

  if (!user) {
    return null;
  }

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsOpen(!isOpen);
    setActiveSubMenu(null);
  };

  // Optimistic UI toggle handlers for default lists
  // TASK A: Now passes enrichment for instant metadata display on WatchlistPage
  const handleDefaultAction = async (
    e: React.MouseEvent,
    slug: 'watchlist' | 'watched' | 'favorites'
  ) => {
    e.stopPropagation();
    e.preventDefault();

    const title = movie.title || '';
    const poster = movie.poster_path || undefined;
    const year = movie.release_date ? parseInt(movie.release_date.slice(0, 4)) : undefined;

    // TASK A: Build enrichment from movie object for instant WatchlistPage metadata
    const filmplRating = calculateOurRating(movie.imdb_rating, movie.rt_rating);
    const enrichment: MediaEnrichment = {
      genre_ids: movie.genre_ids,
      runtime: movie.runtime,
      imdb_rating: movie.imdb_rating,
      rt_rating: movie.rt_rating,
      filmpl_rating: filmplRating ?? undefined,
      popularity: movie.popularity,
      original_language: movie.original_language,
      vote_count: movie.vote_count,
    };

    // Capture current state BEFORE toggle for toast message
    const wasWatched = isWatched(movie.id, mediaType);
    const wasInWatchlist = isInWatchlist(movie.id, mediaType);
    const wasInFavorites = isInFavorites(movie.id, mediaType);

    // Use optimistic toggles - they return immediately (UI updates instantly)
    // and handle API calls in the background with automatic rollback on failure
    try {
      if (slug === 'watched') {
        await toggleWatched(movie.id, mediaType, title, poster, year, enrichment);
        toast.success(wasWatched ? 'Removed from watched' : 'Marked as watched');
      } else if (slug === 'watchlist') {
        await toggleWatchlist(movie.id, mediaType, title, poster, year, enrichment);
        toast.success(wasInWatchlist ? 'Removed from Watchlist' : 'Added to Watchlist');
      } else if (slug === 'favorites') {
        await toggleFavorites(movie.id, mediaType, title, poster, year, enrichment);
        toast.success(wasInFavorites ? 'Removed from Favorites' : 'Added to Favorites');
      }
    } catch (err) {
      // Errors are handled by the optimistic UI system with automatic rollback
      console.error('Error toggling list status:', err);
      toast.error('Something went wrong');
    }
  };

  // Phase 5: SPAM-SAFE custom list toggle using setListItemDesired
  // Uses explicit desiredInList (no toggle semantics), per-item abort, last-intent-wins
  // Only shows toast on real state flips (didChange: true)
  const handleCustomListAction = async (e: React.MouseEvent, listId: string) => {
    e.stopPropagation();
    e.preventDefault();

    const title = movie.title || '';
    const poster = movie.poster_path || undefined;
    const year = movie.release_date ? parseInt(movie.release_date.slice(0, 4)) : undefined;

    // Capture current state to determine desired state
    const wasInList = isInCustomList(listId);
    const listName = customLists.find(l => l.id === listId)?.name || 'list';

    // Build enrichment for instant WatchlistPage metadata
    const filmplRating = calculateOurRating(movie.imdb_rating, movie.rt_rating);
    const enrichment: MediaEnrichment = {
      genre_ids: movie.genre_ids,
      runtime: movie.runtime,
      imdb_rating: movie.imdb_rating,
      rt_rating: movie.rt_rating,
      filmpl_rating: filmplRating ?? undefined,
      popularity: movie.popularity,
      original_language: movie.original_language,
      vote_count: movie.vote_count,
    };

    try {
      // SPAM-SAFE: Use explicit desiredInList (inverse of current state)
      // Per-item abort ensures last click wins, no toast spam
      const result = await setListItemDesired(
        listId,
        movie.id,
        mediaType,
        title,
        !wasInList, // desiredInList: if was in list, we want out; if not, we want in
        poster,
        year,
        enrichment
      );

      // TASK E: Only show toast on REAL state flips (not superseded/no-op)
      if (result.didChange) {
        toast.success(result.direction === 'added' ? `Added to ${listName}` : `Removed from ${listName}`);
      }
    } catch (err) {
      console.error('Error setting list membership:', err);
      if (err instanceof ApiError && err.code === 'SOCIAL_TIER_REQUIRED') {
        setUpgradeModalFeature('Adding to custom lists');
      } else {
        toast.error('Something went wrong');
      }
    }
    // NOTE: No invalidateMembership here - mutation onSettled handles it (single invalidation point)
  };

  // Handle list creation - add movie to new list using optimistic UI
  const handleListCreated = (newList: { id: string; name: string; description?: string; visibility: string; media_filter: MediaFilter }) => {
    const title = movie.title || '';
    const poster = movie.poster_path || undefined;
    const year = movie.release_date ? parseInt(movie.release_date.slice(0, 4)) : undefined;

    // Use optimistic toggleListItem to add to new list
    toggleListItem(newList.id, movie.id, mediaType, title, poster, year);

    // Close the dropdown menu
    setActiveSubMenu(null);
    setIsOpen(false);
  };

  const handleRateClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsOpen(false);
    setActiveSubMenu(null);
    onRateClick?.();
  };

  const handleAskFilmply = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsOpen(false);
    setActiveSubMenu(null);
    const movieTitle = movie.title || 'this movie';
    openWithQuestion(`Would I like "${movieTitle}"? Based on my taste, should I watch it?`);
    navigate('/filmply');
  };

  const handleRecommend = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsOpen(false);
    setActiveSubMenu(null);
    setShowRecommendModal(true);
  };

  const handleShare = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsOpen(false);
    setActiveSubMenu(null);
    setShowShareModal(true);
  };

  // Dropdown menu content rendered via portal
  const dropdownMenu = isOpen && createPortal(
    <div
      ref={menuRef}
      className="absolute w-56 bg-[#0F1419]/95 backdrop-blur-xl rounded-xl border border-[#14b8a6]/20 shadow-2xl shadow-black/50 overflow-visible z-40 will-change-transform"
      style={{ top: 0, left: 0, opacity: 0 }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Teal glow overlay - z-0 */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#14b8a6]/5 to-transparent pointer-events-none rounded-xl z-0" />
      {/* Content - z-10 */}
      <div className="relative py-2 z-10">
        {/* Mark as Watched - uses optimistic UI (instant updates) */}
        <button
          onClick={(e) => handleDefaultAction(e, 'watched')}
          className={`w-full flex items-center gap-3 px-4 py-2.5 hover:bg-[#14b8a6]/10 transition-colors group`}
        >
          {isWatched(movie.id, mediaType) ? (
            <Check className="w-4 h-4 text-[#14b8a6]" />
          ) : (
            <Eye className="w-4 h-4 text-white/60 group-hover:text-white" />
          )}
          <span className={isWatched(movie.id, mediaType) ? 'text-[#14b8a6]' : 'text-white/90'}>{isWatched(movie.id, mediaType) ? 'Watched' : 'Mark as Watched'}</span>
        </button>

        {/* Divider */}
        <div className="my-2 mx-4 border-t border-white/10" />

        {/* Add to... (with submenu) */}
        <div className="relative">
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setActiveSubMenu(activeSubMenu === 'addTo' ? null : 'addTo');
            }}
            className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-[#14b8a6]/10 transition-colors group"
          >
            <span className="flex items-center gap-3">
              <Plus className="w-4 h-4 text-white/60 group-hover:text-white" />
              <span className="text-white/90">Add to...</span>
            </span>
            <ChevronRight className={`w-4 h-4 text-white/60 transition-transform ${activeSubMenu === 'addTo' ? 'rotate-90' : ''}`} />
          </button>

          {/* Add to Submenu */}
          {activeSubMenu === 'addTo' && (
            <div className={`absolute top-0 ${submenuSide === 'right' ? 'left-full ml-2' : 'right-full mr-2'} w-52 bg-[#0F1419]/95 backdrop-blur-xl rounded-xl border border-[#14b8a6]/20 shadow-2xl shadow-black/50 overflow-hidden z-50`}>
              {/* Teal glow overlay */}
              <div className="absolute inset-0 bg-gradient-to-b from-[#14b8a6]/5 to-transparent pointer-events-none rounded-xl z-0" />
              <div className="relative py-2 max-h-80 overflow-y-auto z-10">
                {/* Watchlist - uses optimistic UI */}
                <button
                  onClick={(e) => handleDefaultAction(e, 'watchlist')}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-[#14b8a6]/10 transition-colors group"
                >
                  {isInWatchlist(movie.id, mediaType) ? (
                    <Check className="w-4 h-4 text-[#14b8a6]" />
                  ) : (
                    <Bookmark className="w-4 h-4 text-white/60 group-hover:text-white" />
                  )}
                  <span className={isInWatchlist(movie.id, mediaType) ? 'text-[#14b8a6]' : 'text-white/90'}>{isInWatchlist(movie.id, mediaType) ? 'In Watchlist' : 'Watchlist'}</span>
                </button>

                {/* Favourites - uses optimistic UI */}
                <button
                  onClick={(e) => handleDefaultAction(e, 'favorites')}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-[#14b8a6]/10 transition-colors group"
                >
                  <Heart className={`w-4 h-4 ${isInFavorites(movie.id, mediaType) ? 'text-red-400 fill-current' : 'text-white/60 group-hover:text-white'}`} />
                  <span className={isInFavorites(movie.id, mediaType) ? 'text-red-400' : 'text-white/90'}>{isInFavorites(movie.id, mediaType) ? 'Favourited' : 'Favourites'}</span>
                </button>

                {/* Divider before custom lists */}
                {customLists.length > 0 && <div className="my-2 mx-4 border-t border-white/10" />}

                {/* Custom Lists - uses batch membership hook (single query for all) */}
                {/* TASK D: Removed grey pending UX - buttons always interactive */}
                {/* TASK 1: Uses isInCustomList (optimistic cache first, server fallback) */}
                {customLists.map((list: Watchlist) => {
                  const inList = isInCustomList(list.id);
                  return (
                    <button
                      key={list.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        if (isFree) { setUpgradeModalFeature('Adding to custom lists'); setIsOpen(false); setActiveSubMenu(null); return; }
                        handleCustomListAction(e, list.id);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 transition-colors group hover:bg-[#14b8a6]/10"
                    >
                      {inList ? (
                        <Check className="w-4 h-4 text-[#14b8a6]" />
                      ) : (
                        <List className="w-4 h-4 text-white/60 group-hover:text-white" />
                      )}
                      <span className={`truncate ${inList ? 'text-[#14b8a6]' : 'text-white/90'}`}>
                        {inList ? `In ${list.name}` : list.name}
                      </span>
                    </button>
                  );
                })}

                {/* Divider before create new */}
                <div className="my-2 mx-4 border-t border-white/10" />

                {/* Create New List */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    if (isFree) { setUpgradeModalFeature('Creating custom lists'); setIsOpen(false); setActiveSubMenu(null); return; }
                    setShowCreateListModal(true);
                    setIsOpen(false);
                    setActiveSubMenu(null);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-[#14b8a6]/10 transition-colors"
                >
                  <Plus className="w-4 h-4 text-[#14b8a6]" />
                  <span className="text-[#14b8a6]">Create New List</span>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="my-2 mx-4 border-t border-white/10" />

        {/* Rate */}
        <button
          onClick={handleRateClick}
          className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-[#14b8a6]/10 transition-colors group"
        >
          <Star className={`w-4 h-4 ${userRating ? 'text-yellow-400 fill-yellow-400' : 'text-white/60 group-hover:text-white'}`} />
          <span className={userRating ? 'text-yellow-400' : 'text-white/90'}>{userRating ? `Your Rating: ${userRating}/10` : 'Rate'}</span>
        </button>

        {/* Recommend */}
        <button
          onClick={handleRecommend}
          className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-[#14b8a6]/10 transition-colors group"
        >
          <Send className="w-4 h-4 text-white/60 group-hover:text-white" />
          <span className="text-white/90">Recommend</span>
        </button>

        {/* Share */}
        <button
          onClick={handleShare}
          className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-[#14b8a6]/10 transition-colors group"
        >
          <Share2 className="w-4 h-4 text-white/60 group-hover:text-white" />
          <span className="text-white/90">Share</span>
        </button>

        {/* Divider */}
        <div className="my-2 mx-4 border-t border-white/10" />

        {/* Ask Filmply */}
        <button
          onClick={handleAskFilmply}
          className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-[#14b8a6]/10 transition-colors"
        >
          <Sparkles className="w-4 h-4 text-[#14b8a6]" />
          <div>
            <span className="text-[#14b8a6] font-medium">Ask Filmply</span>
            <p className="text-xs text-white/50">Would I like this?</p>
          </div>
        </button>
      </div>
    </div>,
    document.body
  );

  return (
    <>
      <div className={`relative ${className}`}>
        {/* Quick Action Button */}
        <button
          ref={buttonRef}
          onClick={handleToggle}
          className={`
            w-8 h-8 rounded-full flex items-center justify-center
            transition-all duration-200
            ${isOpen ? 'bg-teal-500 text-white rotate-45' : 'bg-black/60 text-white hover:bg-teal-500'}
          `}
          title="Quick actions"
        >
          <Plus className="w-5 h-5" />
        </button>

        {/* Dropdown rendered via portal */}
        {dropdownMenu}
      </div>

      {/* Create List Modal */}
      <CreateListModal
        isOpen={showCreateListModal}
        onClose={() => setShowCreateListModal(false)}
        onCreated={handleListCreated}
        createWatchlist={createWatchlist}
      />

      {/* Recommend Modal */}
      <RecommendModal
        isOpen={showRecommendModal}
        onClose={() => setShowRecommendModal(false)}
        media={{
          mediaId: movie.id,
          mediaType: mediaType,
          title: movie.title || '',
          poster: movie.poster_path || undefined,
          year: movie.release_date ? parseInt(movie.release_date.slice(0, 4)) : undefined,
        }}
      />

      {/* Share Modal */}
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        url={buildShareUrl(`/${mediaType}/${movie.id}`)}
        title={`${movie.title || ''}${movie.release_date ? ` (${movie.release_date.slice(0, 4)})` : ''}`}
        image={
          movie.poster_path?.startsWith('http')
            ? movie.poster_path
            : movie.poster_path
              ? `https://image.tmdb.org/t/p/w500${movie.poster_path}`
              : undefined
        }
      />

      <SocialUpgradeModal
        isOpen={!!upgradeModalFeature}
        onClose={() => setUpgradeModalFeature(null)}
        feature={upgradeModalFeature || ''}
      />
    </>
  );
}
